import { v4 as uuid } from 'uuid'

import { baseTrackingUrl } from 'embed/constants/config'
import Api from 'common/services/Api'
import Parent from 'common/services/Parent'
import Session from 'common/services/Session'

import {
  captureFacebookPixelTrackingEventError,
  captureTrackingAppnexusIdWarning,
  captureTrackingEventError,
  captureTrackingImpressionError,
  captureTrackingWithNoUuidError,
} from 'embed/constants/errors'
import FacebookPixelService from '../FacebookPixelService'

export interface TrackingEvent {
  action: string
  data: object
  timestamp: Date
}

export class Tracker {
  interactionUuid = uuid()
  private initialized = false
  private trackingUuid: Promise<string>
  private appnexusData: Promise<{ uid?: string }>
  private readonly window: Window

  constructor(window: Window) {
    this.window = window
  }

  init(name: string, dispatchLeadInitialized: (leadUuid: string) => void): void {
    const timeoutMs = 800
    this.initialized = true

    if (!this.appnexusData) {
      this.appnexusData = new Promise<{ uid?: string }>((resolve, reject) => {
        this.getAppnexusDataAsync(resolve, reject)

        const timer = setTimeout(() => reject(new Error(`Reached timeout of ${timeoutMs} milliseconds`)), timeoutMs)
      })

      this.appnexusData
        .then(() => {
          this.trackEvent({
            action: 'appnexus-id',
            data: {
              timeoutMs,

              succeded: true,
            },
            timestamp: new Date(),
          })
        })
        .catch((error: Error) => {
          this.trackEvent({
            action: 'appnexus-id',
            data: {
              timeoutMs,

              error: error && error.message && error.message.toString ? error.message.toString() : error,
              succeded: false,
            },
            timestamp: new Date(),
          })
        })
    }

    if (Session.trackingUuid) {
      this.trackingUuid = Promise.resolve(Session.trackingUuid)
    } else {
      this.trackingUuid = Promise.all([
        Parent.getData(),
        this.appnexusData.catch((error) => {
          captureTrackingAppnexusIdWarning(error)
          return ({} as unknown) as { uid?: string | undefined }
        }),
      ]).then(([parentData, appnexusData]) =>
        Api.get(`${baseTrackingUrl}/impression`, {
          appnexusId: appnexusData.uid,
          asset: name,
          companyUuid: Session.companyUuid,
          leadUuid: Session.getLeadUuid(),
          referrer: parentData.referrer,
        })
          .then((res) => {
            const newTrackingUuid = res.data._evtId
            Session.trackingUuid = newTrackingUuid
            return newTrackingUuid
          })
          .catch(async (err) => {
            captureTrackingImpressionError(
              name,
              Session.companyUuid || 'companyUuid missing',
              Session.getLeadUuid(),
              parentData.referrer || 'parentData.referrer missing',
              err,
            )
            return Promise.reject(err)
          }),
      )
    }
  }

  getAppnexusDataAsync(onSuccess: (data: { uid: string }) => void, onError: () => void): void {
    const callbackName = `_appNexusCb_${Math.floor(Math.random() * 1000)}`
    this.window[callbackName] = onSuccess

    const scriptElement = this.window.document.createElement('script')
    scriptElement.src = `//ib.adnxs.com/getuidp?callback=${callbackName}`
    scriptElement.onerror = onError
    scriptElement.onabort = onError
    this.window.document.getElementsByTagName('head')[0].appendChild(scriptElement)
  }

  getTrackingUuid(): Promise<string> {
    if (!this.initialized) {
      throw new Error('Tracker is not initialized')
    }
    return this.trackingUuid
  }

  trackEvent(event: TrackingEvent): void {
    this.getTrackingUuid()
      .then((trackingUuid) =>
        Api.post(`${baseTrackingUrl}/events`, {
          events: [
            {
              data: event.data || {},
              name: event.action,
              timestamp: event.timestamp.toISOString(),
            },
          ],
          interactionUuid: this.interactionUuid,
          trackingUuid,
        }).catch((err) => {
          captureTrackingEventError(event.action, trackingUuid, err)
        }),
      )
      .catch((err) => {
        captureTrackingWithNoUuidError(event.action, err)
      })

    FacebookPixelService.sendEvent(`${event.action}`, event.data).catch((err) =>
      captureFacebookPixelTrackingEventError(event.action, err),
    )
  }
}

export default new Tracker(window)
